import React, { useRef, useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { useTheme } from '../../contexts/ThemeContext';

interface VideoRecorderProps {
  isOpen: boolean;
  onClose: () => void;
  onRecordComplete: (videoBlob: Blob, thumbnail?: string) => void;
  maxDuration?: number; // em segundos, padrão 5 minutos
}

export const VideoRecorder: React.FC<VideoRecorderProps> = ({
  isOpen,
  onClose,
  onRecordComplete,
  maxDuration = 300, // 5 minutos
}) => {
  const { theme } = useTheme();
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen) {
      requestPermissions();
    } else {
      stopRecording();
      cleanup();
    }

    return () => {
      cleanup();
    };
  }, [isOpen]);

  const requestPermissions = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      
      setHasPermission(true);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err: any) {
      console.error('Erro ao solicitar permissões:', err);
      setHasPermission(false);
      setError('Não foi possível acessar a câmera e/ou microfone. Verifique as permissões.');
    }
  };

  const startRecording = () => {
    if (!streamRef.current) {
      setError('Stream não disponível');
      return;
    }

    try {
      chunksRef.current = [];
      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: 'video/webm;codecs=vp8,opus',
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        setRecordedBlob(blob);
        const url = URL.createObjectURL(blob);
        setRecordedUrl(url);
        setIsRecording(false);
        
        // Gerar thumbnail
        generateThumbnail(blob).then(thumbnail => {
          // Thumbnail será passado no onRecordComplete
        }).catch(err => {
          console.warn('Erro ao gerar thumbnail:', err);
        });
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setDuration(0);

      // Iniciar contador de duração
      durationIntervalRef.current = setInterval(() => {
        setDuration((prev) => {
          const newDuration = prev + 1;
          if (newDuration >= maxDuration) {
            stopRecording();
            return maxDuration;
          }
          return newDuration;
        });
      }, 1000);
    } catch (err: any) {
      console.error('Erro ao iniciar gravação:', err);
      setError('Erro ao iniciar gravação: ' + err.message);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    }
  };

  const generateThumbnail = async (videoBlob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        video.currentTime = 1; // Frame no segundo 1
      };

      video.onseeked = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (blob) {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve(reader.result as string);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          } else {
            reject(new Error('Erro ao gerar thumbnail'));
          }
        }, 'image/jpeg', 0.8);
      };

      video.onerror = reject;
      video.src = URL.createObjectURL(videoBlob);
    });
  };

  const handleConfirm = async () => {
    if (recordedBlob) {
      const thumbnail = recordedUrl ? await generateThumbnail(recordedBlob).catch(() => undefined) : undefined;
      onRecordComplete(recordedBlob, thumbnail);
      handleCancel();
    }
  };

  const handleCancel = () => {
    stopRecording();
    cleanup();
    setRecordedBlob(null);
    setRecordedUrl(null);
    setDuration(0);
    setError(null);
    onClose();
  };

  const cleanup = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Gravar Vídeo"
      footer={
        <>
          <Button variant="secondary" onClick={handleCancel}>
            Cancelar
          </Button>
          {recordedBlob && (
            <Button onClick={handleConfirm}>
              Usar Vídeo
            </Button>
          )}
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: theme.colors.errorContainer || '#fee2e2',
            color: theme.colors.onErrorContainer || '#991b1b',
            borderRadius: '8px',
            width: '100%',
            textAlign: 'center',
          }}>
            {error}
          </div>
        )}

        {hasPermission === false && (
          <div style={{
            padding: '12px',
            backgroundColor: theme.colors.errorContainer || '#fee2e2',
            color: theme.colors.onErrorContainer || '#991b1b',
            borderRadius: '8px',
            width: '100%',
            textAlign: 'center',
          }}>
            Permissões de câmera e microfone necessárias. Por favor, permita o acesso e recarregue.
          </div>
        )}

        {hasPermission && !recordedBlob && (
          <>
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={{
                width: '100%',
                maxWidth: '640px',
                borderRadius: '8px',
                backgroundColor: '#000',
              }}
            />
            
            {isRecording && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                backgroundColor: theme.colors.errorContainer || '#fee2e2',
                borderRadius: '8px',
                width: '100%',
                justifyContent: 'center',
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: '#ef4444',
                  animation: 'pulse 1s infinite',
                }} />
                <span style={{ color: theme.colors.onErrorContainer || '#991b1b', fontWeight: '600' }}>
                  Gravando... {formatDuration(duration)} / {formatDuration(maxDuration)}
                </span>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', width: '100%', justifyContent: 'center' }}>
              {!isRecording ? (
                <Button onClick={startRecording} style={{ minWidth: '120px' }}>
                  Iniciar Gravação
                </Button>
              ) : (
                <Button onClick={stopRecording} variant="secondary" style={{ minWidth: '120px' }}>
                  Parar Gravação
                </Button>
              )}
            </div>
          </>
        )}

        {recordedBlob && recordedUrl && (
          <>
            <video
              src={recordedUrl}
              controls
              style={{
                width: '100%',
                maxWidth: '640px',
                borderRadius: '8px',
                backgroundColor: '#000',
              }}
            />
            <div style={{ fontSize: '14px', color: theme.colors.textMuted }}>
              Duração: {formatDuration(duration)} | Tamanho: {(recordedBlob.size / (1024 * 1024)).toFixed(2)} MB
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="secondary" onClick={() => {
                setRecordedBlob(null);
                setRecordedUrl(null);
                setDuration(0);
                requestPermissions();
              }}>
                Gravar Novamente
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};




